import { useEffect, useState, FC } from 'react'
import { Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Api from 'services/Api'

import { Loading } from 'components/UI'
import { Badge, Button as MerinioButton, Card, List } from 'ui'
import { Format } from 'services/Dates'
import { getUserName } from 'services/Utilities'
import { refreshCounter } from 'store/actions/refresh'
import { t } from 'translations'

type ContactEventsResponse = {
  _id: string
  pendingConfirmation: boolean
  events: MData.Event
  method: string
  confirmedBy: string
  type?: 'confirmation' | 'cancellation'
}

type Props = {
  _id: string
  isTimeOff?: boolean
}

const ConfirmationStatus: FC<Props> = ({ _id, isTimeOff }) => {
  const dispatch = useDispatch()
  const { confirmationStatus: confirmationStatusRefresh } = useSelector((state: MStore.RootState) => state.refresh)
  const { defaultTimeZone } = useSelector((state: MStore.RootState) => state.settings)

  const [contactEvents, setContactEvents] = useState<ContactEventsResponse[] | undefined>(undefined)
  useEffect(() => {
    const loadContactEvents = async () => {
      const { result, success }: { success: boolean; result: Record<string, ContactEventsResponse[]> } = await Api.post('schedule/contact/events', {
        eventIds: [_id],
        ghostIds: [_id]
      })
      if (!success) return setContactEvents([])

      setContactEvents(result[_id] || [])
    }
    loadContactEvents()
  }, [_id, confirmationStatusRefresh])

  const getStyling = eventKey => {
    switch (eventKey) {
      case 'confirmed':
        return ['check-square', '']
      case 'opened':
        return ['envelope-open', '']
      case 'delivered':
        return ['envelope', '']
      case 'failed':
        return ['exclamation-square', ' text-danger']
      case 'noPhone':
        return ['phone-slash', ' text-danger']
      default:
        return ['', '']
    }
  }

  if (!contactEvents) return <Loading />

  if (!contactEvents.length)
    return (
      <Card className="mt-2">
        <Card.Body>{t(`manager.schedule.EDIT_MODAL.${isTimeOff ? 'TIMEOFF_NOT_CONFIRMED' : 'SHIFT_NOT_CONFIRMED'}`)}</Card.Body>
      </Card>
    )

  return (
    <List className="mt-2">
      {contactEvents.reverse().map(({ _id, pendingConfirmation, events, method, confirmedBy, type = 'confirmation' }) => {
        const eventKeys = Object.keys(events)
        return (
          <List.Item className="d-flex justify-content-between" key={_id}>
            <div>
              {eventKeys.map((eventKey, index) => {
                const [icon, className] = getStyling(eventKey)
                return (
                  <div key={index} className={className}>
                    {icon !== '' ? <i className={`fad fa-${icon} mr-1`}></i> : null}
                    <Trans t={t} i18nKey={`manager.schedule.EDIT_MODAL.CONFIRMATION_EVENTS.${eventKey.toUpperCase()}_${method.toUpperCase()}`}>
                      <b>{{ name: getUserName(confirmedBy) }}</b>
                      <b>{{ time: Format(events[eventKey], { outFormat: 'time', isUTC: true, timeZone: defaultTimeZone }) }}</b>
                      {{ date: Format(events[eventKey], { outFormat: 'humanDate', isUTC: true, timeZone: defaultTimeZone }) }}
                    </Trans>
                  </div>
                )
              })}
              {pendingConfirmation ? (
                <MerinioButton
                  color="primary"
                  size="small"
                  className="mt-1"
                  onClick={async () => {
                    await Api.put('/schedule/confirm', { scheduleContactEventIds: [_id] })
                    dispatch(refreshCounter('confirmationStatus'))
                  }}
                >
                  {t('manager.schedule.EDIT_MODAL.CONFIRM_FOR_EMPLOYEE')}
                </MerinioButton>
              ) : null}
            </div>
            <div>
              <Badge pill variant={type === 'cancellation' ? 'danger' : 'success'}>
                {t(`manager.schedule.EDIT_MODAL.CONFIRMATION_EVENTS.${type.toUpperCase()}`)}
              </Badge>
            </div>
          </List.Item>
        )
      })}
    </List>
  )
}

export default ConfirmationStatus
